import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext, useField } from 'formik';
import { Card, CardContent, FormControl, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ContentInput, PreviewUrlMutation, usePreviewUrlMutation } from 'api';
import { Loaders } from 'components';
import { useNotify } from 'hooks';
import { USER_FEED_ID } from 'constants/index';

import { getEmbed, hasPhoto, isEmbed } from 'features/content/helpers';
import { convertUrlImageToPhoto } from '../../helpers';
import { usePostContext } from '../../PostContext';

import PhotoPreview from '../Link/PhotoPreview';
import PhotoUpload from '../Link/PhotoUpload';
import Thumbnails from '../Link/Thumbnails';
import EditLinkData from '../Link/EditLinkData';

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'flex',
    minHeight: 225,
    borderTop: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  card: {
    borderTop: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  cardBody: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  chooseImage: {
    paddingTop: theme.spacing(),
  },
  chooseImageTitle: {
    paddingBottom: theme.spacing(0.5),
  },
  loader: {
    height: 300,
  },
  selected: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
}));

const UrlPreview: FC = () => {
  const classes = useStyles();
  const notify = useNotify();

  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext<ContentInput>();
  const { dispatch } = usePostContext();
  const [urlField] = useField('content_object.url');

  const { url_metadata_override } = values.content_object;

  useEffect(() => {
    if (!!urlField.value) {
      previewUrlMutation({ variables: { url: urlField.value, group_id: USER_FEED_ID } });
    }
  }, [urlField.value]);

  const [previewUrlMutation, { data, loading, error }] = usePreviewUrlMutation({
    onError: () => notify.error({ message: t('posting:invalidUrl') }),
    onCompleted: handleCompleted,
  });

  function handleCompleted(mutationData: PreviewUrlMutation) {
    const { is_engageable, is_shareable, content_object } = mutationData.processContent;
    const { images, title, description } = content_object.url_metadata;

    setFieldValue('is_engageable', is_engageable);
    setFieldValue('is_shareable', is_shareable);

    setFieldValue('content_object.url_metadata_override.title', title || '');
    setFieldValue('content_object.url_metadata_override.description', description || '');

    if (!hasPhoto(values) && !!images) {
      setFieldValue('content_object.photos', [convertUrlImageToPhoto(images[0])]);
    }

    if (is_engageable) {
      dispatch({ type: 'engagePost', value: true });
    }
  }

  function renderLoader() {
    return (
      <div className={classes.loader}>
        <Loaders.Standard />
      </div>
    );
  }

  function renderError() {
    return (
      <Paper className={classes.paper}>
        <Grid container alignItems="center" justify="center">
          <Grid item>
            <Typography variant="body2">{t('posting:previewUnavailable')}</Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  function renderEmbed() {
    return (
      <Paper className={classes.paper}>
        <Grid container alignItems="center" justify="center">
          <Grid item>{getEmbed(data.processContent)}</Grid>
        </Grid>
      </Paper>
    );
  }

  function renderPreview() {
    if (isEmbed(data.processContent)) {
      return renderEmbed();
    }

    const { content_object } = data.processContent;
    const { url_metadata } = content_object;
    const { images } = url_metadata;

    return (
      <Card className={classes.card}>
        <PhotoPreview />
        <CardContent classes={{ root: classes.chooseImage }}>
          <Typography color="textSecondary" className={classes.chooseImageTitle}>
            {t('posting:chooseImage')}
          </Typography>
          <Grid container spacing={1}>
            <Thumbnails images={images} />
            <Grid item>
              <PhotoUpload />
            </Grid>
          </Grid>
        </CardContent>
        <CardContent classes={{ root: classes.cardBody }}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item xs zeroMinWidth>
              <Typography title={url_metadata_override.title} noWrap variant="h6">
                {url_metadata_override.title}
              </Typography>
            </Grid>
            <EditLinkData />
          </Grid>
          <Typography color="textSecondary">{url_metadata_override.description}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <FormControl fullWidth>
      {error && renderError()}
      {loading && renderLoader()}
      {data?.processContent && renderPreview()}
    </FormControl>
  );
};

export default UrlPreview;
